import React, { useState, useEffect } from "react";
import {
  getProjectDetails,
  updateProjectDetail,
} from "../StepperService";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../../../../helpers/methods";
import { YhButton, YhInput, YhSelect } from "../../../../../ui";

interface CustomProps {
  nextStep?: () => void | undefined;
  backStep?: () => void | undefined;
  userId: String;
  idd: string;
  data?: any;
  fromSettingOrLogout?: any;
}

interface Stage {
  percentage: string;
  milestone: string;
  subLabel: string;
}

interface Plan {
  planName: string;
  planType: "Custom" | "CLP";
  stages: Stage[];
}

const emptyStage = (): Stage => ({
  percentage: "",
  milestone: "",
  subLabel: "",
});

const emptyPlan = (index: number): Plan => ({
  planName: `Plan ${index}`,
  planType: "Custom",
  stages: [emptyStage()],
});

const planTypeOptions = [
  { label: "Custom (% split)", value: "Custom" },
  { label: "CLP (Construction Linked)", value: "CLP" },
];

const PaymentPlan = (props: CustomProps) => {
  const [projectName, setProjectName] = useState<string>("");
  const [plans, setPlans] = useState<Plan[]>([emptyPlan(1)]);
  const [activePlanIndex, setActivePlanIndex] = useState(0);

  useEffect(() => {
    loadProjectDetails();
  }, [props?.idd]);

  const loadProjectDetails = async () => {
    if (props?.data?.paymentPlan?.length) {
      setProjectName(props.data.project_name || "");
      mapPlans(props.data.paymentPlan);
    }
    if (!props?.idd) return;
    try {
      const response = await getProjectDetails(props.idd);
      setProjectName(response?.data?.project_name || "");
      if (response?.data?.paymentPlan?.length) {
        mapPlans(response.data.paymentPlan);
      }
    } catch (error: any) {
      openErrorNotification(
        error?.message || "Failed to load payment plan"
      );
    }
  };

  const mapPlans = (list: any[]) => {
    const loaded: Plan[] = list.map((p: any) => ({
      planName: p.planName || "Plan",
      planType: p.planType === "CLP" ? "CLP" : "Custom",
      stages:
        (p.stages || []).length > 0
          ? (p.stages || []).map((s: any) => ({
              percentage: s.percentage != null ? String(s.percentage) : "",
              milestone: s.milestone || "",
              subLabel: s.subLabel || "",
            }))
          : [emptyStage()],
    }));
    setPlans(loaded.length ? loaded : [emptyPlan(1)]);
    setActivePlanIndex(0);
  };

  const handleAddPlan = () => {
    setPlans((prev) => {
      const next = [...prev, emptyPlan(prev.length + 1)];
      setActivePlanIndex(next.length - 1);
      return next;
    });
  };

  const handleRemovePlan = (planIndex: number) => {
    setPlans((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((_, idx) => idx !== planIndex);
      setActivePlanIndex((active) =>
        Math.min(active > planIndex ? active - 1 : active, next.length - 1)
      );
      return next;
    });
  };

  const handlePlanNameChange = (planIndex: number, value: string) => {
    setPlans((prev) =>
      prev.map((plan, idx) =>
        idx === planIndex ? { ...plan, planName: value } : plan
      )
    );
  };

  const handlePlanTypeChange = (
    planIndex: number,
    value: "Custom" | "CLP"
  ) => {
    setPlans((prev) =>
      prev.map((plan, idx) =>
        idx === planIndex ? { ...plan, planType: value } : plan
      )
    );
  };

  const handleAddStage = (planIndex: number) => {
    setPlans((prev) =>
      prev.map((plan, idx) =>
        idx === planIndex
          ? { ...plan, stages: [...plan.stages, emptyStage()] }
          : plan
      )
    );
  };

  const handleRemoveStage = (planIndex: number, stageIndex: number) => {
    setPlans((prev) =>
      prev.map((plan, idx) =>
        idx === planIndex
          ? {
              ...plan,
              stages: plan.stages.filter((_, sIdx) => sIdx !== stageIndex),
            }
          : plan
      )
    );
  };

  const handleStageChange = (
    planIndex: number,
    stageIndex: number,
    field: keyof Stage,
    value: string
  ) => {
    setPlans((prev) =>
      prev.map((plan, idx) => {
        if (idx !== planIndex) return plan;
        const stages = plan.stages.map((stage, sIdx) =>
          sIdx === stageIndex ? { ...stage, [field]: value } : stage
        );
        return { ...plan, stages };
      })
    );
  };

  const getStageTotal = (plan: Plan) =>
    plan.stages.reduce((sum, s) => sum + (Number(s.percentage) || 0), 0);

  const isStageFilled = (stage: Stage) =>
    Boolean(String(stage.percentage || "").trim()) ||
    Boolean(String(stage.milestone || "").trim()) ||
    Boolean(String(stage.subLabel || "").trim());

  /** Custom plan with any stage data entered by the user. */
  const isCustomPlanFilled = (plan: Plan) =>
    plan.planType === "Custom" && plan.stages.some(isStageFilled);

  /** User intentionally set a plan (filled Custom stages or chose CLP). */
  const isPlanIntentional = (plan: Plan) =>
    plan.planType === "CLP" || isCustomPlanFilled(plan);

  const buildPaymentPlanPayload = (source: Plan[]) =>
    source.filter(isPlanIntentional).map((plan) => ({
      planName: plan.planName,
      planType: plan.planType,
      stages:
        plan.planType === "CLP"
          ? []
          : plan.stages
              .filter(isStageFilled)
              .map((s, i) => ({
                percentage: Number(s.percentage) || 0,
                milestone: s.milestone,
                subLabel: s.subLabel,
                sortOrder: i,
              })),
    }));

  const saveAndContinue = async (payloadPlans: Plan[]) => {
    if (!props?.idd) {
      openErrorNotification("Please save project information first");
      return;
    }

    const body = {
      _id: props.idd,
      paymentPlan: buildPaymentPlanPayload(payloadPlans),
    };

    const response = await updateProjectDetail(body);
    if (response?.success) {
      openSuccessNotification(response?.message);
      props.nextStep?.();
    }
  };

  const submitForm = async () => {
    try {
      const filledCustom = plans.filter(isCustomPlanFilled);
      const invalid = filledCustom.find((p) => getStageTotal(p) !== 100);
      if (invalid) {
        openErrorNotification(
          `"${invalid.planName || "Plan"}" total must equal 100% when stages are added`
        );
        return;
      }

      await saveAndContinue(plans);
    } catch (error: any) {
      openErrorNotification(error?.message || "Failed to save payment plan");
    }
  };

  const skipStep = async () => {
    try {
      await saveAndContinue([]);
    } catch (error: any) {
      openErrorNotification(error?.message || "Failed to skip payment plan");
    }
  };

  const activePlan = plans[activePlanIndex];
  const stageTotal = activePlan ? getStageTotal(activePlan) : 0;
  const activePlanHasStages = activePlan
    ? isCustomPlanFilled(activePlan)
    : false;
  const totalIsValid = !activePlanHasStages || stageTotal === 100;

  return (
    <div className="w-full">
      <div className="mb-6 flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#EEF0FF] text-sm font-bold text-[#A02448]">
          ☰
        </span>
        <div>
          <h2 className="text-lg font-bold tracking-tight text-[#111827]">
            Payment Plan{" "}
            <span className="text-sm font-medium text-[#9CA3AF]">(optional)</span>
          </h2>
          <p className="mt-0.5 text-sm text-[#6B7280]">
            Optionally add payment plans buyers can choose from. You can skip
            this step and continue.
          </p>
        </div>
      </div>

      {/* Live preview */}
      <div className="mb-6 rounded-2xl border border-[#EEF0F5] bg-white p-4 sm:p-5">
        <h3 className="mb-3 text-sm font-bold text-[#A02448]">
          {projectName ? `${projectName} Payment Plan` : "Payment Plan"}
        </h3>
        <div className="mb-4 h-px bg-[#EEF0F5]" />

        <div className="mb-4 flex flex-wrap gap-2">
          {plans.map((plan, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setActivePlanIndex(idx)}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${
                idx === activePlanIndex
                  ? "bg-[#A02448] text-white"
                  : "bg-[#EEF0FF] text-[#A02448] hover:bg-[#E0E4FF]"
              }`}
            >
              {plan.planName || `Plan ${idx + 1}`}
            </button>
          ))}
        </div>

        {activePlan?.planType === "CLP" ? (
          <p className="text-sm italic text-[#6B7280]">
            Construction Linked Plan — payment is tied to construction
            milestones rather than a fixed percentage split.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-xl bg-[#F5F6FF] px-4 py-5">
            <div className="flex min-w-max items-stretch gap-0">
              {(activePlan?.stages || []).map((stage, sIdx) => (
                <React.Fragment key={sIdx}>
                  <div className="flex min-w-[120px] flex-col items-center px-3 text-center">
                    <span className="text-lg font-extrabold text-[#111827]">
                      {stage.percentage ? `${stage.percentage}%` : "—"}
                    </span>
                    <span className="mt-1 text-xs font-semibold text-[#374151]">
                      {stage.milestone || "Milestone"}
                    </span>
                    {stage.subLabel ? (
                      <span className="mt-0.5 text-[11px] text-[#9CA3AF]">
                        {stage.subLabel}
                      </span>
                    ) : null}
                  </div>
                  {sIdx < (activePlan?.stages.length || 0) - 1 ? (
                    <div className="flex items-center">
                      <span className="h-px w-8 border-t border-dashed border-[#A5B4FC]" />
                      <span className="text-[#A02448]">›</span>
                      <span className="h-px w-8 border-t border-dashed border-[#A5B4FC]" />
                    </div>
                  ) : null}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Plans editor */}
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-base font-bold text-[#111827]">Plans</h3>
        <YhButton variant="secondary" size="sm" onClick={handleAddPlan}>
          + Add Plan
        </YhButton>
      </div>

      <div className="mb-4 flex gap-4 overflow-x-auto border-b border-[#EEF0F5]">
        {plans.map((plan, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => setActivePlanIndex(idx)}
            className={`relative shrink-0 pb-2.5 text-sm font-semibold transition ${
              idx === activePlanIndex
                ? "text-[#A02448]"
                : "text-[#9CA3AF] hover:text-[#6B7280]"
            }`}
          >
            {plan.planName || `Plan ${idx + 1}`}
            {idx === activePlanIndex ? (
              <span className="absolute bottom-0 left-0 h-0.5 w-full bg-[#A02448]" />
            ) : null}
          </button>
        ))}
      </div>

      {activePlan ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_1fr_auto] md:items-end">
            <YhInput
              label="Plan Name"
              placeholder="e.g. Plan 1, Option A"
              value={activePlan.planName}
              onChange={(e) =>
                handlePlanNameChange(activePlanIndex, e.target.value)
              }
            />
            <YhSelect
              label="Plan Type"
              placeholder="Select"
              showOptionIcon={false}
              value={activePlan.planType}
              options={planTypeOptions}
              onChange={(e) =>
                handlePlanTypeChange(
                  activePlanIndex,
                  e.target.value as "Custom" | "CLP"
                )
              }
            />
            {plans.length > 1 ? (
              <YhButton
                variant="secondary"
                size="sm"
                onClick={() => handleRemovePlan(activePlanIndex)}
              >
                Remove Plan
              </YhButton>
            ) : (
              <div className="hidden md:block" />
            )}
          </div>

          {activePlan.planType === "Custom" ? (
            <>
              <div className="space-y-3">
                {activePlan.stages.map((stage, sIdx) => (
                  <div
                    key={sIdx}
                    className="grid grid-cols-1 items-end gap-3 rounded-xl border border-[#EEF0F5] p-3 md:grid-cols-[120px_1fr_1fr_auto]"
                  >
                    <YhInput
                      label="Percentage"
                      placeholder="10"
                      type="number"
                      value={stage.percentage}
                      endAdornment={
                        <span className="text-xs font-semibold text-[#6B7280]">
                          %
                        </span>
                      }
                      onChange={(e) =>
                        handleStageChange(
                          activePlanIndex,
                          sIdx,
                          "percentage",
                          e.target.value
                        )
                      }
                    />
                    <YhInput
                      label="Milestone"
                      placeholder="e.g. Down payment"
                      value={stage.milestone}
                      onChange={(e) =>
                        handleStageChange(
                          activePlanIndex,
                          sIdx,
                          "milestone",
                          e.target.value
                        )
                      }
                    />
                    <YhInput
                      label="Sub-label (optional)"
                      placeholder="e.g. At sales launch"
                      value={stage.subLabel}
                      onChange={(e) =>
                        handleStageChange(
                          activePlanIndex,
                          sIdx,
                          "subLabel",
                          e.target.value
                        )
                      }
                    />
                    {activePlan.stages.length > 1 ? (
                      <button
                        type="button"
                        onClick={() =>
                          handleRemoveStage(activePlanIndex, sIdx)
                        }
                        className="mb-1 flex h-10 w-10 items-center justify-center rounded-lg text-[#DC2626] transition hover:bg-[#FEF2F2]"
                        aria-label="Remove stage"
                      >
                        ×
                      </button>
                    ) : (
                      <div className="hidden md:block md:h-10 md:w-10" />
                    )}
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => handleAddStage(activePlanIndex)}
                  className="text-sm font-semibold text-[#A02448] hover:underline"
                >
                  + Add Stage
                </button>
                <p
                  className={`text-sm font-semibold ${
                    !activePlanHasStages
                      ? "text-[#9CA3AF]"
                      : totalIsValid
                        ? "text-[#059669]"
                        : "text-[#DC2626]"
                  }`}
                >
                  Total: {stageTotal}%
                  {activePlanHasStages && !totalIsValid
                    ? " (must equal 100% when stages are added)"
                    : ""}
                </p>
              </div>
            </>
          ) : (
            <p className="rounded-xl bg-[#F8F9FF] px-4 py-3 text-sm text-[#6B7280]">
              CLP plans do not use a fixed % split. Stages are managed against
              construction milestones.
            </p>
          )}
        </div>
      ) : null}

      <div className="mt-8 flex items-center justify-between gap-3 border-t border-[#EEF0F5] pt-5">
        <YhButton variant="secondary" onClick={() => props.backStep?.()}>
          ← Back
        </YhButton>
        <div className="flex items-center gap-2">
          <YhButton variant="secondary" onClick={skipStep}>
            Skip
          </YhButton>
          <YhButton variant="primary" onClick={submitForm}>
            Save & Continue →
          </YhButton>
        </div>
      </div>
    </div>
  );
};

export default PaymentPlan;
